import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Image,
  Placeholder,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBagShopping,
  faBoxOpen,
  faCalendar,
  faCircleInfo,
  faCopy,
  faEnvelope,
  faLocationDot,
  faPhone,
  faStore,
  faTrash,
  faTruck,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../Components/Common/PageHeader";
import "./orderDetail.scss";

const fmtMoney = (val, currency) => {
  if (val === undefined || val === null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return `${val} ${currency || ""}`.trim();
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();
};

const fmtDate = (val) => (val ? Utils.dateFormat(val, false) : "—");

const safeStr = (v) => (v === undefined || v === null || v === "" ? "—" : v);

const copyToClipboard = (text) => {
  if (!text) return;
  try {
    navigator.clipboard.writeText(String(text));
    toast.success("Đã sao chép");
  } catch (e) {
    toast.error("Không thể sao chép");
  }
};

const StatusBadge = ({ status }) => {
  if (!status) return <Badge bg="secondary">—</Badge>;
  const s = String(status).toLowerCase();
  let bg = "secondary";
  if (["completed", "delivered", "fulfilled"].includes(s)) bg = "success";
  else if (["processing", "shipped", "awaiting_shipment", "in_transit"].includes(s)) bg = "info";
  else if (["pending", "on-hold", "awaiting_collection"].includes(s)) bg = "warning";
  else if (["cancelled", "canceled", "failed", "refunded"].includes(s)) bg = "danger";
  return <Badge bg={bg} className="text-uppercase">{status}</Badge>;
};

/** Normalize platform-specific order shape into a common view-model */
function normalize(order, flatform) {
  if (!order) return null;
  const platform = flatform || (order.payment ? "tiktokshop" : "woocommerce");

  // line items
  const rawItems = Array.isArray(order.line_items) ? order.line_items : [];
  const items = rawItems.map((it) => {
    if (platform === "tiktokshop") {
      return {
        id: it.id,
        name: it.product_name,
        sku: it.seller_sku || it.sku_id,
        variant: it.sku_name,
        image: it.sku_image,
        qty: it.quantity || 1,
        price: it.sale_price,
        original: it.original_price,
        currency: it.currency,
        status: it.display_status,
      };
    }
    // woo
    return {
      id: it.id,
      name: it.name,
      sku: it.sku,
      variant: (it.meta_data || [])
        .filter((m) => !String(m.key || "").startsWith("_"))
        .map((m) => `${m.display_key || m.key}: ${m.display_value || m.value}`)
        .join(", "),
      image: it.image?.src,
      qty: it.quantity || 1,
      price: it.price,
      original: it.subtotal && it.quantity ? Number(it.subtotal) / Number(it.quantity) : null,
      currency: order.currency,
      status: null,
    };
  });

  if (platform === "tiktokshop") {
    const r = order.recipient_address || {};
    const p = order.payment || {};
    return {
      platform,
      number: order.order_id || order.id,
      status: order.status,
      createdAt: order.create_time,
      currency: p.currency,
      buyerEmail: order.buyer_email,
      buyerMessage: order.buyer_message,
      paymentMethod: order.payment_method_name,
      shippingType: order.shipping_type,
      shippingProvider: order.shipping_provider,
      tracking: order.tracking_number || order.shipping_provider_id,
      deliveryOption: order.delivery_option_name,
      autoCancelAt: order.cancel_order_sla_time,
      shippingDueAt: order.shipping_due_time,
      recipient: {
        name: r.name,
        phone: r.phone_number,
        address: r.full_address,
      },
      billing: null,
      items,
      totals: [
        { label: "Sản phẩm (gốc)", value: p.original_total_product_price },
        { label: "Phí vận chuyển", value: p.original_shipping_fee },
        { label: "Giảm giá vận chuyển", value: p.shipping_fee_platform_discount },
        { label: "Thuế", value: p.tax },
        { label: "Giảm giá nền tảng", value: p.platform_discount },
        { label: "Giảm giá người bán", value: p.seller_discount },
      ],
      totalAmount: p.total_amount,
    };
  }

  // woocommerce
  const b = order.billing || {};
  const s = order.shipping || {};
  const totals = [
    { label: "Tạm tính", value: order.total ? Number(order.total) - Number(order.total_tax || 0) - Number(order.shipping_total || 0) + Number(order.discount_total || 0) : null },
    { label: "Vận chuyển", value: order.shipping_total },
    { label: "Thuế", value: order.total_tax },
    { label: "Giảm giá", value: order.discount_total ? -Math.abs(Number(order.discount_total)) : null },
  ];
  return {
    platform,
    number: order.number || order.id,
    status: order.status,
    createdAt: order.date_created_gmt || order.date_created,
    paidAt: order.date_paid_gmt || order.date_paid,
    completedAt: order.date_completed_gmt || order.date_completed,
    currency: order.currency,
    buyerEmail: b.email,
    buyerMessage: order.customer_note,
    paymentMethod: order.payment_method_title || order.payment_method,
    transactionId: order.transaction_id,
    shippingType: order.shipping_lines?.[0]?.method_title,
    tracking: null,
    recipient: {
      name: [s.first_name, s.last_name].filter(Boolean).join(" "),
      phone: s.phone || b.phone,
      address: [s.address_1, s.address_2, s.city, s.state, s.postcode, s.country].filter(Boolean).join(", "),
    },
    billing: {
      name: [b.first_name, b.last_name].filter(Boolean).join(" "),
      phone: b.phone,
      email: b.email,
      address: [b.address_1, b.address_2, b.city, b.state, b.postcode, b.country].filter(Boolean).join(", "),
    },
    items,
    totals,
    totalAmount: order.total,
  };
}

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    Utils.setPageTitle("Order detail");
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const r = await Utils._apiRequest({
          path: Enums.endpoints.orders.DETAIL,
          pathParams: { id },
          type: "GET",
        });
        if (cancelled) return;
        if (r?.errors) throw new Error(r.errors.message || "Lỗi tải đơn hàng");
        setDoc(r?.data || null);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (id) load();
    return () => { cancelled = true; };
  }, [id]);

  const vm = useMemo(() => (doc ? normalize(doc.data, doc.flatform) : null), [doc]);

  const handleDelete = async () => {
    if (!doc?._id) return;
    if (!window.confirm(`Xoá đơn hàng "${vm?.number}"?\nHành động này không thể hoàn tác.`)) {
      return;
    }
    setDeleting(true);
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.orders.DELETE_ONE,
        pathParams: { id: doc._id },
        type: "DELETE",
      });
      if (!r?.ok) throw new Error(r?.errors?.message || "Xoá thất bại");
      toast.success("Đã xoá đơn hàng");
      navigate("/orders");
    } catch (err) {
      toast.error(err.message);
      setDeleting(false);
    }
  };

  return (
    <main className="page-main order-detail-page">
      <div className="main-container container">
        <PageHeader
          title={vm ? `Đơn #${vm.number}` : "Đơn hàng"}
          description={doc?.flatform ? `Nền tảng: ${doc.flatform}` : "Chi tiết đơn hàng"}
          icon={faBagShopping}
          breadcrumb={
            <Link to="/orders" className="text-decoration-none">
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />Quay lại danh sách
            </Link>
          }
        >
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />Quay lại
            </Button>
            <Button variant="outline-danger" onClick={handleDelete} disabled={loading || deleting}>
              {deleting ? <Spinner size="sm" animation="border" className="me-2" /> : <FontAwesomeIcon icon={faTrash} className="me-2" />}
              Xoá đơn
            </Button>
          </div>
        </PageHeader>

        {error ? (
          <Alert variant="danger">{error}</Alert>
        ) : loading ? (
          <Row className="g-3">
            <Col md={8}>
              <Card><Card.Body><Placeholder animation="glow"><Placeholder xs={8} /><br /><Placeholder xs={6} /><br /><Placeholder xs={10} /></Placeholder></Card.Body></Card>
            </Col>
            <Col md={4}>
              <Card><Card.Body><Placeholder animation="glow"><Placeholder xs={6} /><br /><Placeholder xs={9} /></Placeholder></Card.Body></Card>
            </Col>
          </Row>
        ) : !vm ? (
          <Alert variant="warning">Không tìm thấy đơn hàng.</Alert>
        ) : (
          <Row className="g-3">
            {/* LEFT */}
            <Col lg={8}>
              {/* Summary card */}
              <Card className="mb-3 order-summary-card">
                <Card.Body>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="mb-0">Đơn #{vm.number}</h4>
                      <Button variant="link" size="sm" className="p-0" onClick={() => copyToClipboard(vm.number)} title="Copy mã đơn">
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                      <StatusBadge status={vm.status} />
                    </div>
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faCalendar} className="me-1" />
                      {fmtDate(vm.createdAt)}
                    </small>
                  </div>

                  <Row className="g-3 small">
                    <Col sm={6} md={4}>
                      <div className="text-muted text-uppercase fs-7">Nền tảng</div>
                      <div className="fw-semibold">{safeStr(vm.platform)}</div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted text-uppercase fs-7">Thanh toán</div>
                      <div className="fw-semibold">{safeStr(vm.paymentMethod)}</div>
                    </Col>
                    <Col sm={6} md={4}>
                      <div className="text-muted text-uppercase fs-7">Tổng tiền</div>
                      <div className="fw-semibold text-primary fs-5">{fmtMoney(vm.totalAmount, vm.currency)}</div>
                    </Col>
                    {vm.tracking && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Tracking</div>
                        <div className="fw-semibold">
                          {vm.tracking}
                          <Button variant="link" size="sm" className="p-0 ms-2" onClick={() => copyToClipboard(vm.tracking)}>
                            <FontAwesomeIcon icon={faCopy} />
                          </Button>
                        </div>
                      </Col>
                    )}
                    {vm.shippingType && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Loại vận chuyển</div>
                        <div className="fw-semibold">{vm.shippingType}</div>
                      </Col>
                    )}
                    {vm.deliveryOption && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Tuỳ chọn giao</div>
                        <div className="fw-semibold">{vm.deliveryOption}</div>
                      </Col>
                    )}
                    {vm.transactionId && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Mã giao dịch</div>
                        <div className="fw-semibold text-break">{vm.transactionId}</div>
                      </Col>
                    )}
                    {vm.paidAt && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Đã thanh toán</div>
                        <div className="fw-semibold">{fmtDate(vm.paidAt)}</div>
                      </Col>
                    )}
                    {vm.completedAt && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Hoàn tất</div>
                        <div className="fw-semibold">{fmtDate(vm.completedAt)}</div>
                      </Col>
                    )}
                    {vm.autoCancelAt && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Auto cancel</div>
                        <div className="fw-semibold">{fmtDate(vm.autoCancelAt)}</div>
                      </Col>
                    )}
                    {vm.shippingDueAt && (
                      <Col sm={6} md={4}>
                        <div className="text-muted text-uppercase fs-7">Hạn giao hàng</div>
                        <div className="fw-semibold">{fmtDate(vm.shippingDueAt)}</div>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>

              {vm.buyerMessage ? (
                <Alert variant="warning" className="d-flex align-items-start gap-2">
                  <FontAwesomeIcon icon={faCircleInfo} className="mt-1" />
                  <div><strong>Lời nhắn của khách:</strong> {vm.buyerMessage}</div>
                </Alert>
              ) : null}

              {/* Items */}
              <Card className="mb-3">
                <Card.Header className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faBoxOpen} />
                  <strong>Sản phẩm ({vm.items.length})</strong>
                </Card.Header>
                <Table responsive hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th width={80}>Ảnh</th>
                      <th>Sản phẩm</th>
                      <th width={80} className="text-center">SL</th>
                      <th width={140} className="text-end">Đơn giá</th>
                      <th width={140} className="text-end">Thành tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vm.items.length === 0 ? (
                      <tr><td colSpan={5} className="text-center text-muted">Không có sản phẩm</td></tr>
                    ) : vm.items.map((it, i) => {
                      const lineTotal = Number(it.price || 0) * Number(it.qty || 1);
                      return (
                        <tr key={it.id || i}>
                          <td>
                            {it.image ? (
                              <Image
                                rounded
                                width={64}
                                height={64}
                                src={Utils.getMediaImg({ url: it.image, width: 128, height: 128 })}
                                alt={it.name}
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              <div className="bg-light rounded" style={{ width: 64, height: 64 }} />
                            )}
                          </td>
                          <td>
                            <div className="fw-semibold">{safeStr(it.name)}</div>
                            <div className="small text-muted">
                              {it.sku ? <>SKU: <code>{it.sku}</code></> : null}
                              {it.variant ? <> &nbsp;•&nbsp; {it.variant}</> : null}
                            </div>
                            {it.status ? <div className="small mt-1"><Badge bg="light" text="dark">{it.status}</Badge></div> : null}
                          </td>
                          <td className="text-center">{it.qty}</td>
                          <td className="text-end">
                            <div>{fmtMoney(it.price, it.currency || vm.currency)}</div>
                            {it.original && Number(it.original) > Number(it.price) ? (
                              <small className="text-muted text-decoration-line-through">
                                {fmtMoney(it.original, it.currency || vm.currency)}
                              </small>
                            ) : null}
                          </td>
                          <td className="text-end fw-semibold">{fmtMoney(lineTotal, it.currency || vm.currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card>

              {/* Totals breakdown */}
              <Card className="mb-3">
                <Card.Header><strong>Chi tiết thanh toán</strong></Card.Header>
                <Table className="mb-0">
                  <tbody>
                    {vm.totals.filter((t) => t.value !== null && t.value !== undefined && t.value !== "").map((t, i) => (
                      <tr key={i}>
                        <th className="ps-3">{t.label}</th>
                        <td className="text-end pe-3">{fmtMoney(t.value, vm.currency)}</td>
                      </tr>
                    ))}
                    <tr className="table-light">
                      <th className="ps-3">Tổng cộng</th>
                      <td className="text-end pe-3 fw-bold fs-5 text-primary">{fmtMoney(vm.totalAmount, vm.currency)}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card>
            </Col>

            {/* RIGHT */}
            <Col lg={4}>
              <Card className="mb-3">
                <Card.Header className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faStore} /><strong>Cửa hàng</strong>
                </Card.Header>
                <Card.Body>
                  <div className="fw-semibold">{safeStr(doc?.store?.storeName)}</div>
                  {doc?.store?.storeUrl ? (
                    <a href={doc.store.storeUrl} target="_blank" rel="noreferrer" className="small">
                      {doc.store.storeUrl}
                    </a>
                  ) : null}
                  <div className="small text-muted mt-1">
                    Nền tảng: {safeStr(doc?.store?.flatform || doc?.flatform)}
                  </div>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faUser} /><strong>Khách hàng</strong>
                </Card.Header>
                <Card.Body className="small">
                  <div className="mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                    {safeStr(vm.buyerEmail)}
                  </div>
                  {vm.recipient?.phone ? (
                    <div className="mb-2">
                      <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                      {vm.recipient.phone}
                    </div>
                  ) : null}
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faTruck} /><strong>Địa chỉ giao</strong>
                </Card.Header>
                <Card.Body className="small">
                  <div className="fw-semibold">{safeStr(vm.recipient?.name)}</div>
                  {vm.recipient?.phone ? <div>{vm.recipient.phone}</div> : null}
                  <div className="text-muted">
                    <FontAwesomeIcon icon={faLocationDot} className="me-1" />
                    {safeStr(vm.recipient?.address)}
                  </div>
                </Card.Body>
              </Card>

              {vm.billing && (vm.billing.address || vm.billing.email) ? (
                <Card className="mb-3">
                  <Card.Header><strong>Địa chỉ thanh toán</strong></Card.Header>
                  <Card.Body className="small">
                    <div className="fw-semibold">{safeStr(vm.billing.name)}</div>
                    {vm.billing.phone ? <div>{vm.billing.phone}</div> : null}
                    {vm.billing.email ? <div>{vm.billing.email}</div> : null}
                    <div className="text-muted">{safeStr(vm.billing.address)}</div>
                  </Card.Body>
                </Card>
              ) : null}

              {currentUser?.admin && doc?.user ? (
                <Card className="mb-3">
                  <Card.Header><strong>Người sở hữu</strong></Card.Header>
                  <Card.Body className="small">
                    <div className="fw-semibold">{safeStr(doc.user.fullName)}</div>
                    <div className="text-muted">{safeStr(doc.user.email)}</div>
                  </Card.Body>
                </Card>
              ) : null}
            </Col>
          </Row>
        )}
      </div>
    </main>
  );
};

export default OrderDetailPage;
